import { Haptics, ImpactStyle } from '@capacitor/haptics'

import React, { useEffect, useState } from 'react'

import { FormFieldDTO } from '../models/FormFieldDTO.model'
import { getReviewChoiceLabel, LAST_WORKED_INPUT_NAME } from '../utils/reviewFormNavigation'

type ReviewYesNoFieldProps = {
  field: FormFieldDTO
  fieldValue?: unknown
  onChange: (fieldId: number | string, value: unknown) => void
  validationError?: string | JSX.Element
  compactLabel?: boolean
}

function getChoiceGridClass(field: FormFieldDTO, choiceCount: number): string {
  if (field.inputName === LAST_WORKED_INPUT_NAME) {
    return 'grid-cols-2 md:grid-cols-3 w-full'
  }
  if (choiceCount === 3) {
    return 'grid-cols-3 max-w-md'
  }
  if (choiceCount >= 5) {
    return 'grid-cols-2 max-w-lg'
  }
  return 'grid-cols-2 max-w-xs'
}

function getChoiceButtonClass(isSelected: boolean): string {
  return isSelected
    ? 'rounded-lg border border-aqua-mint bg-aqua-mint px-4 py-3 text-sm font-semibold text-white transition-all'
    : 'rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-semibold text-graphite-grey transition-all hover:bg-gray-50 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-royal-indigo'
}

export const ReviewYesNoField: React.FC<ReviewYesNoFieldProps> = ({
  field,
  fieldValue,
  onChange,
  validationError,
  compactLabel = false,
}) => {
  const [selected, setSelected] = useState<string | null>(
    fieldValue !== undefined && fieldValue !== null && fieldValue !== '' ? String(fieldValue) : null,
  )
  const choiceCount = field.choices?.length ?? 0

  useEffect(() => {
    if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
      setSelected(String(fieldValue))
    }
  }, [fieldValue])

  const handleSelect = (value: string) => {
    setSelected(value)
    onChange(field.id, value)
    Haptics.impact({ style: ImpactStyle.Light })
  }

  return (
    <div id={`review-input-${field.id}`} className="mb-6">
      {compactLabel ? (
        <label className="mb-2 block text-sm font-semibold text-graphite-grey">{field.label}</label>
      ) : (
        <h2 className="text-xl font-bold text-graphite-grey md:text-2xl">{field.label}</h2>
      )}
      <div className={`${compactLabel ? 'mt-2' : 'mt-3'} grid w-full gap-2 ${getChoiceGridClass(field, choiceCount)}`}>
        {field.choices?.map((choice) => {
          const isSelected = selected === choice.value
          const choiceLabel = getReviewChoiceLabel(field, choice)

          return (
            <button
              key={choice.value}
              type="button"
              aria-label={choiceLabel}
              aria-pressed={isSelected}
              className={`${compactLabel ? 'min-w-0 w-full' : 'min-w-[5rem]'} ${getChoiceButtonClass(isSelected)}`}
              onClick={() => handleSelect(choice.value)}
            >
              {choiceLabel}
            </button>
          )
        })}
      </div>
      {validationError && (
        <div className="mt-3 text-sm text-red-600" tabIndex={-1}>
          {validationError}
        </div>
      )}
    </div>
  )
}
